import { useQuery } from "@tanstack/react-query";
import { Badge, cn, isRawRank, type RawRank } from "@tcg/ui";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { usePos } from "@/app/context";
import { CameraCapture } from "@/components/CameraCapture";
import { MoneyText } from "@/components/MoneyText";
import { NumPad } from "@/components/NumPad";
import { ItemBadges } from "@/features/sale/ItemBadges";
import { call } from "@/lib/api";
import type { BuylistLine, BuylistLineAdd } from "@/lib/types";
import { uploadCardPhoto } from "@/lib/upload";

import { RankPicker } from "./RankPicker";

export type ConditionType = BuylistLineAdd["conditionType"];

export interface LineDraft {
  conditionType: ConditionType;
  title: string;
  rawRank: RawRank | null;
  gradingCompany: string;
  grade: string;
  certNumber: string;
  quantity: number;
  offer: string;
  sale: string;
  features: string;
  front: Blob | null;
  back: Blob | null;
}

export const EMPTY_DRAFT: LineDraft = {
  conditionType: "raw_card",
  title: "",
  rawRank: null,
  gradingCompany: "PSA",
  grade: "",
  certNumber: "",
  quantity: 1,
  offer: "",
  sale: "",
  features: "",
  front: null,
  back: null,
};

/** What still stops the line from being added (one reason each — shown next to the button). */
export function draftProblems(draft: LineDraft): string[] {
  const out: string[] = [];
  if (!draft.title.trim()) {
    out.push("title");
  }
  if (draft.conditionType === "raw_card" && !draft.rawRank) {
    out.push("rank");
  }
  if (
    draft.conditionType === "graded_card" &&
    (!draft.gradingCompany.trim() || !draft.grade.trim())
  ) {
    out.push("grade");
  }
  if (!draft.offer) {
    out.push("offer");
  }
  return out;
}

export function draftBody(draft: LineDraft, photos: string[]): BuylistLineAdd {
  const offer = Number.parseInt(draft.offer || "0", 10);
  const sale = draft.sale ? Number.parseInt(draft.sale, 10) : null;
  return {
    title: draft.title.trim(),
    conditionType: draft.conditionType,
    offerPrice: offer,
    quantity: draft.conditionType === "sealed" ? draft.quantity : 1,
    ...(draft.conditionType === "raw_card" && draft.rawRank ? { rawRank: draft.rawRank } : {}),
    ...(draft.conditionType === "graded_card"
      ? {
          gradingCompany: draft.gradingCompany.trim(),
          grade: draft.grade.trim(),
          ...(draft.certNumber.trim() ? { certNumber: draft.certNumber.trim() } : {}),
        }
      : {}),
    ...(sale ? { salePrice: sale } : {}),
    ...(draft.features.trim() ? { features: draft.features.trim() } : {}),
    ...(photos.length ? { photos } : {}),
  };
}

const TYPES: ConditionType[] = ["raw_card", "graded_card", "sealed"];

/**
 * One card to buy (T07-31, design/05 §3.4): what it is (素 / graded / 未開封), its name (search
 * the shop's catalogue or type it), **Rank A–D for a raw card** (required — the line stays red and
 * cannot be added without it), grade + cert for a slab, the price the shop pays (NumPad), the sale
 * price later on the shelf, 特徴 for the 古物台帳, and optional front / back photos. 相場参考 stays
 * empty (market prices come in a later phase — no invented number).
 */
export function QuoteLineForm({
  busy,
  onAdd,
  error,
}: {
  busy: boolean;
  onAdd: (body: BuylistLineAdd) => Promise<boolean>;
  error: string | null;
}) {
  const { t } = useTranslation();
  const { api, media } = usePos();
  const [draft, setDraft] = useState<LineDraft>(EMPTY_DRAFT);
  const [uploading, setUploading] = useState(false);
  const [query, setQuery] = useState("");
  const set = (changes: Partial<LineDraft>) => setDraft((d) => ({ ...d, ...changes }));
  const problems = draftProblems(draft);

  useEffect(() => {
    const timer = setTimeout(() => setQuery(draft.title.trim()), 250);
    return () => clearTimeout(timer);
  }, [draft.title]);
  const suggestions = useQuery({
    queryKey: ["pos-search", "list", "buylist", query],
    queryFn: () =>
      call(() => api.GET("/v1/pos/search", { params: { query: { q: query, limit: 5 } } })),
    enabled: query.length >= 2,
    staleTime: 30_000,
  });

  const add = async () => {
    if (problems.length || busy || uploading) {
      return;
    }
    setUploading(true);
    try {
      const photos: string[] = [];
      for (const photo of [draft.front, draft.back]) {
        if (photo) {
          photos.push((await uploadCardPhoto(media, photo)).assetId);
        }
      }
      const ok = await onAdd(draftBody(draft, photos));
      if (ok) {
        setDraft({ ...EMPTY_DRAFT, conditionType: draft.conditionType });
      }
    } finally {
      setUploading(false);
    }
  };

  // a scanner (or a typed code + Enter) in the name field: a slab's cert → a graded line with the
  // cert; a code the shop knows (label / JAN / cert in stock) → its name, kind, Rank / grade
  const scanned = async (code: string) => {
    const value = code.trim();
    if (!value) {
      return;
    }
    try {
      const hit = await call(() =>
        api.GET("/v1/pos/lookup", { params: { query: { code: value } } }),
      );
      const v = hit.variant;
      set({
        title: v.title.ja ?? v.title.en ?? value,
        conditionType:
          v.productType === "graded_single"
            ? "graded_card"
            : v.productType === "sealed"
              ? "sealed"
              : "raw_card",
        rawRank: isRawRank(v.rawRank) ? v.rawRank : null,
        gradingCompany: v.grading?.company ?? draft.gradingCompany,
        grade: v.grading?.grade ?? "",
        certNumber: v.grading?.certNumber ?? (/^\d{7,10}$/.test(value) ? value : ""),
        sale: String(v.price.amount),
      });
    } catch {
      if (/^\d{7,10}$/.test(value)) {
        set({ title: "", conditionType: "graded_card", rawRank: null, certNumber: value });
      }
    }
  };

  const red = draft.conditionType === "raw_card" && !draft.rawRank;

  return (
    <section
      aria-label={t("buylist.new_line")}
      data-invalid={red ? "true" : undefined}
      className={cn(
        "flex flex-col gap-3 rounded-lg border p-3",
        // no tinted background: the red sentence inside must keep 4.5:1 on white
        red && "border-2 border-status-problem",
      )}
    >
      <fieldset className="flex flex-wrap gap-2">
        <legend className="mb-1 font-medium">{t("buylist.kind")}</legend>
        {TYPES.map((type) => (
          <button
            key={type}
            type="button"
            aria-pressed={draft.conditionType === type}
            onClick={() =>
              set({ conditionType: type, rawRank: type === "raw_card" ? draft.rawRank : null })
            }
            className={cn(
              "min-h-pos-touch rounded-md border px-4 text-lg",
              draft.conditionType === type && "border-primary bg-primary text-primary-foreground",
            )}
          >
            {t(`buylist.kind_${type}`)}
          </button>
        ))}
      </fieldset>
      <label className="flex flex-col gap-1">
        <span className="font-medium">{t("buylist.title")}</span>
        <input
          value={draft.title}
          onChange={(e) => set({ title: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void scanned(e.currentTarget.value);
            }
          }}
          autoComplete="off"
          enterKeyHint="search"
          placeholder={t("buylist.title_placeholder")}
          className="min-h-pos-touch rounded-md border px-3 text-lg"
        />
      </label>
      {suggestions.data?.data.length && query === draft.title.trim() ? (
        <ul aria-label={t("buylist.suggestions")} className="flex flex-wrap gap-2">
          {suggestions.data.data.map((row) => (
            <li key={row.variantId}>
              <button
                type="button"
                onClick={() =>
                  set({
                    title: row.title.ja ?? row.title.en ?? draft.title,
                    conditionType:
                      row.productType === "graded_single"
                        ? "graded_card"
                        : row.productType === "sealed"
                          ? "sealed"
                          : "raw_card",
                    rawRank: isRawRank(row.rawRank) ? row.rawRank : null,
                    gradingCompany: row.grading?.company ?? draft.gradingCompany,
                    grade: row.grading?.grade ?? "",
                    sale: String(row.price.amount),
                  })
                }
                className="flex min-h-12 items-center gap-2 rounded-md border px-3 text-left"
              >
                {row.thumb ? (
                  <img
                    src={row.thumb}
                    alt=""
                    loading="lazy"
                    className="size-10 rounded object-cover"
                  />
                ) : null}
                <span>{row.title.ja ?? row.title.en}</span>
                <ItemBadges productType={row.productType} rawRank={row.rawRank ?? null} size="sm" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {draft.conditionType === "raw_card" ? (
        <RankPicker value={draft.rawRank} onChange={(rank) => set({ rawRank: rank })} />
      ) : null}
      {draft.conditionType === "graded_card" ? (
        <div className="grid grid-cols-3 gap-2">
          <label className="flex flex-col gap-1">
            <span className="font-medium">{t("buylist.grader")}</span>
            <select
              value={draft.gradingCompany}
              onChange={(e) => set({ gradingCompany: e.target.value })}
              className="min-h-pos-touch rounded-md border px-3 text-lg"
            >
              {["PSA", "BGS", "CGC", "ARS", "その他"].map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-medium">{t("buylist.grade")}</span>
            <input
              value={draft.grade}
              onChange={(e) => set({ grade: e.target.value })}
              inputMode="decimal"
              className="min-h-pos-touch rounded-md border px-3 text-lg"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-medium">{t("buylist.cert")}</span>
            <input
              value={draft.certNumber}
              onChange={(e) => set({ certNumber: e.target.value })}
              inputMode="numeric"
              className="min-h-pos-touch rounded-md border px-3 text-lg"
            />
          </label>
        </div>
      ) : null}
      {draft.conditionType === "sealed" ? (
        <label className="flex max-w-xs flex-col gap-1">
          <span className="font-medium">{t("buylist.quantity")}</span>
          <input
            type="number"
            min={1}
            max={50}
            value={draft.quantity}
            onChange={(e) =>
              set({
                quantity: Math.max(1, Math.min(50, Number.parseInt(e.target.value || "1", 10))),
              })
            }
            className="min-h-pos-touch rounded-md border px-3 text-lg"
          />
          <span className="text-sm text-muted-foreground">{t("buylist.quantity_note")}</span>
        </label>
      ) : null}
      <div className="grid gap-3 md:grid-cols-[minmax(0,16rem)_1fr]">
        <NumPad
          label={t("buylist.offer_price")}
          value={draft.offer}
          onChange={(v) => set({ offer: v })}
          money
        />
        <div className="flex flex-col gap-3">
          <p className="rounded-md bg-muted p-3">
            <span className="font-medium">{t("buylist.market")}</span>: {t("buylist.market_none")}
          </p>
          <label className="flex flex-col gap-1">
            <span className="font-medium">{t("buylist.sale_price")}</span>
            <input
              value={draft.sale}
              onChange={(e) => set({ sale: e.target.value.replace(/\D/g, "") })}
              inputMode="numeric"
              placeholder={t("buylist.sale_price_later")}
              className="min-h-pos-touch rounded-md border px-3 text-lg"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-medium">{t("buylist.features")}</span>
            <input
              value={draft.features}
              onChange={(e) => set({ features: e.target.value })}
              placeholder={t("buylist.features_placeholder")}
              className="min-h-pos-touch rounded-md border px-3 text-lg"
            />
          </label>
          <details>
            <summary className="min-h-12 cursor-pointer py-2 font-medium">
              {t("buylist.photos")}
            </summary>
            <div className="grid gap-3 md:grid-cols-2">
              <CameraCapture
                label={t("buylist.photo_front")}
                photo={draft.front}
                onPhoto={(p) => set({ front: p })}
              />
              <CameraCapture
                label={t("buylist.photo_back")}
                photo={draft.back}
                onPhoto={(p) => set({ back: p })}
              />
            </div>
          </details>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => void add()}
          disabled={problems.length > 0 || busy || uploading}
          aria-describedby={problems.length ? "line-problems" : undefined}
          className="flex min-h-pos-touch items-center gap-2 rounded-md bg-primary px-6 text-lg font-semibold text-primary-foreground disabled:opacity-50"
        >
          <Plus aria-hidden className="size-6" />
          {uploading ? t("app.saving") : t("buylist.add_line")}
        </button>
        {problems.length ? (
          <span id="line-problems" className="text-pos-problem">
            {t(`buylist.problem_${problems[0] ?? "title"}`)}
          </span>
        ) : null}
        {error ? (
          <span role="alert" className="text-pos-problem">
            {error}
          </span>
        ) : null}
      </div>
    </section>
  );
}

/** One card already on the purchase: kind, Rank / grade, the price the shop pays, edit / remove. */
export function QuoteLineRow({
  line,
  editable,
  onEditPrice,
  onRemove,
}: {
  line: BuylistLine;
  editable: boolean;
  onEditPrice: (line: BuylistLine) => void;
  onRemove: (line: BuylistLine) => void;
}) {
  const { t } = useTranslation();
  const title = line.title.ja ?? line.title.en ?? "—";
  return (
    <li className="flex items-center gap-3 border-b px-3 py-2">
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium">{title}</p>
        <span className="flex flex-wrap items-center gap-1">
          <Badge variant="outline">{t(`buylist.kind_${line.conditionType}`)}</Badge>
          <ItemBadges
            productType={
              line.conditionType === "raw_card"
                ? "raw_single"
                : line.conditionType === "graded_card"
                  ? "graded_single"
                  : "sealed"
            }
            rawRank={line.rawRank}
            grading={
              line.gradingCompany
                ? {
                    company: line.gradingCompany,
                    grade: line.grade ?? "",
                    certNumber: line.certNumber,
                  }
                : null
            }
            size="sm"
          />
          {line.features ? (
            <span className="text-sm text-muted-foreground">{line.features}</span>
          ) : null}
        </span>
      </div>
      <MoneyText amount={line.offerPrice} size="lg" />
      {editable ? (
        <>
          <button
            type="button"
            aria-label={t("buylist.edit_price", { title })}
            onClick={() => onEditPrice(line)}
            className="flex size-11 items-center justify-center rounded-md border"
          >
            <Pencil aria-hidden className="size-5" />
          </button>
          <button
            type="button"
            aria-label={t("buylist.remove", { title })}
            onClick={() => onRemove(line)}
            className="flex size-11 items-center justify-center rounded-md border"
          >
            <Trash2 aria-hidden className="size-5" />
          </button>
        </>
      ) : null}
    </li>
  );
}
